<template>
  <div class="input">
    <div
      class="field"
      :class="[{ isLabelAtop, hasLabel: label, isDisabled: disabled }]"
    >
      <label :for="inputId" v-if="label">{{ label }}</label>
      <textarea
        ref="textarea"
        rows="4"
        :name="inputName"
        :id="inputId"
        @focus="isFocused = true"
        @blur="
          isFocused = false;
          $emit('blur');
        "
        @input="inputEvent"
        @change="$emit('update:modelValue', $event.target.value)"
        :value="modelValue"
        :required="required"
        :placeholder="placeholder"
        :disabled="disabled"
        :maxlength="maxlength"
      ></textarea>
    </div>
    <div v-if="message" class="error">{{ message }}</div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  props: {
    label: {
      type: String,
    },
    name: {
      type: String,
    },
    id: {
      type: String,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
    },
    message: {
      type: String,
    },
    autoexpand: {
      type: Boolean,
      default: true,
    },
    modelValue: {
      type: String,
    },
    maxlength: {
      type: Number,
    },
  },
  data() {
    return {
      isFocused: false,
      inputId: "",
      inputName: "",
    };
  },
  created() {
    this.inputId =
      this.id ||
      "id" + window.crypto.getRandomValues(new Uint32Array(1)).toString();
    this.inputName = this.name || this.inputId;
  },
  computed: {
    isLabelAtop(): boolean {
      return this.isFocused || !!this.modelValue;
    },
    validity(): object {
      const inputElement = this.$refs.textarea as HTMLFormElement;
      return inputElement.validity;
    },
  },
  mounted() {
    // fit on mount, in case someone wrote a long fairy tale as prefilled value
    this.fitHeightToContent();
  },
  methods: {
    inputEvent(event: { target: HTMLInputElement }) {
      this.$emit("update:modelValue", event.target.value);

      if (this.autoexpand) {
        this.fitHeightToContent();
      }
    },
    fitHeightToContent() {
      // is method, as computed props don't recalculate for style changes

      const textarea = this.$refs.textarea as HTMLElement;
      textarea.style.height = "inherit";
      const computed = window.getComputedStyle(textarea);
      const isBorderBox =
        computed.getPropertyValue("box-sizing") === "border-box";

      let height =
        parseInt(computed.getPropertyValue("border-top-width"), 10) +
        textarea.scrollHeight +
        parseInt(computed.getPropertyValue("border-bottom-width"), 10);

      // add padding, depending on box-sizing
      if (!isBorderBox) {
        height += parseInt(computed.getPropertyValue("padding-top"), 10);
        height += parseInt(computed.getPropertyValue("padding-bottom"), 10);
      }

      textarea.style.height = height + "px";
    },
  },
});
</script>

<style lang="scss" scoped>
.input {
  position: relative;
  width: 100%;
  color: $color-primary-gray;
}

.field {
  width: 100%;

  &.hasLabel {
    textarea {
      padding-top: 20px;
    }
    &.isLabelAtop {
      label {
        transform: translateY(-12px) scale(0.7);
      }
    }
    &:not(.isLabelAtop) {
      textarea::placeholder {
        color: transparent;
        transition: color 0.2s ease;
      }
    }
  }

  &.isDisabled {
    label {
      cursor: not-allowed;
      color: $color-secondary-bluegray-50;
    }
    textarea {
      cursor: not-allowed;
      border-bottom-color: $color-secondary-bluegray-50;
      &:hover {
        background-color: #f6f6f6;
      }
      &::placeholder {
        color: $color-secondary-bluegray-50;
      }
    }
  }
}

textarea {
  font-size: inherit;
  font-family: inherit;
  transition: background-color 0.2s ease, border 0.2s ease, color 0.2s ease;
  outline: none;
  box-shadow: none;
  height: 100%;
  width: 100%;
  padding-top: 10px;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 20px;
  background-color: #f6f6f6;
  border: 1px solid $color-primary-gray;
  max-width: 100%;
  color: $text-color;
  outline: 1px $color-secondary-darkgray;

  &::placeholder {
    color: $color-primary-gray;
    transition: color 0.2s ease 0.15s;
  }

  &:hover {
    background-color: $color-field-backdrop;
    border-color: 1px solid $color-primary-blue;
    color: $text-color;
    outline: 1px $color-primary-blue;
  }

  &:focus {
    background-color: $color-field-backdrop;
    border: 1px solid $color-primary-blue;
    color:$text-color;
    outline: 2px $color-primary-blue;
  }
}

label {
  position: absolute;
  top: 13px;
  left: 20px;
  transition: transform 0.2s ease;
  transform-origin: left center;
  pointer-events: none;
}

.message {
  font-size: 12px;
  line-height: 24px;
  margin-left: 1px;
  color: $color-secondary-darkgray;
}

.icon {
  position: absolute;
  top: 14px;
  right: 14px;
  outline: none;
}

.error {
  font-weight: bold;
  color: $error-color;
}
</style>
